import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, catchError, take, switchMap } from 'rxjs/operators';
import { EMPTY, of, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { ToastController } from '@ionic/angular';
import { OrderData } from '../../../interfaces';
import { LocationData, Order, Venue } from '../../../models';
import {
  addOrder,
  clearOrders,
  loadOrders,
  newMessageForOrder,
  removeOrder,
  setOrders,
  updateOrder,
} from './open-order.actions';
import { AppState, errorHandler } from '../app.state';
import { OrderService } from '../../../providers/order/order.service';
import { SupportService } from '../../../providers/support/support.service';
import { BeepService } from '../../../providers/audio/beep.service';
import { LoadingService } from '../../../providers/general/loading.service';
import { OrderStatus } from '../../../enums';
import {
  PusherChannel,
  PusherService,
  PusherEvent,
} from '../../../providers/pusher/pusher.service';

/** Returns true when an in-flight load should still apply its result. */
export function shouldApplyOrdersLoad(
  requestGeneration: number,
  currentGeneration: number
): boolean {
  return requestGeneration === currentGeneration;
}

@Injectable()
export class OpenOrderEffects {
  private loadGeneration = 0;

  loadOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadOrders),
      switchMap((payload: { location: LocationData; venue: Venue; hideLoading?: true }) => {
        const generation = this.beginOrdersLoad();
        const { location, venue, hideLoading } = payload;
        this.location = location;
        this.venue = venue;
        if (!venue?.data || !location?._id) {
          this.tearDownOrderListeners();
          this.loader.hide();
          return of(setOrders({ orders: [] }));
        }
        const query = {
          ...(venue.data.shareTabs && { venue: venue.id }),
          ...(!venue.data.shareTabs && { restaurant: location._id }),
        };
        if (!hideLoading) {
          this.loader.show();
        }
        return this.ordersService.getOpenOrders(query).pipe(
          mergeMap((orderResponse: OrderData[]) => {
            if (!shouldApplyOrdersLoad(generation, this.loadGeneration)) {
              return EMPTY;
            }
            this.listenToOrders({ location, venue });
            this.listenToStatusChanges();
            this.listenToMessageReadStatus();
            this.startReloadOrders();
            this.loader.hide();
            return of(
              setOrders({
                orders: orderResponse
                  .filter(
                    orderData =>
                      orderData.status !== OrderStatus.AUTHORIZING &&
                      orderData.status !== OrderStatus.DRAFT &&
                      orderData.status !== OrderStatus.CART
                  )
                  .map(orderData => new Order(orderData)),
              })
            );
          }),
          catchError(error => {
            if (!shouldApplyOrdersLoad(generation, this.loadGeneration)) {
              return EMPTY;
            }
            this.loader.hide();
            return errorHandler(error);
          })
        );
      })
    )
  );

  clearOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clearOrders),
      mergeMap(() => {
        this.beginOrdersLoad();
        this.tearDownOrderListeners();
        this.loader.hide();
        return [setOrders({ orders: [] })];
      })
    )
  );

  private pusherListener: Subscription;

  private statusListener: Subscription;

  private messageListener: Subscription;

  private location: LocationData;

  private venue: Venue;

  private checkingOrdersById: Record<string, OrderData> = {};

  private static interval: any;

  constructor(
    private actions$: Actions,
    private store: Store,
    private ordersService: OrderService,
    private supportService: SupportService,
    private toastCtrl: ToastController,
    private beepService: BeepService,
    private pusherService: PusherService,
    private loader: LoadingService
  ) {}

  private beginOrdersLoad(): number {
    this.loadGeneration += 1;
    return this.loadGeneration;
  }

  private startReloadOrders() {
    if (!OpenOrderEffects.interval) {
      OpenOrderEffects.interval = setInterval(async () => {
        const openOrdersInfo = await this.store
          .select((state: AppState) => state.orders)
          .pipe(take(1))
          .toPromise();
        const { lastUpdated } = openOrdersInfo;
        if (!lastUpdated || lastUpdated.getTime() < new Date().getTime() - 30 * 1000) {
          this.store.dispatch(
            loadOrders({ location: this.location, venue: this.venue, hideLoading: true })
          );
        }
      }, 15 * 1000);
    }
  }

  private static stopReloadOrders() {
    clearInterval(OpenOrderEffects.interval);
    OpenOrderEffects.interval = null;
  }

  private async showMessageToast(order: Order) {
    const toast = await this.toastCtrl.create({
      message: `New message for order ${order.data.orderNumber}.`,
      color: 'secondary',
      duration: 5000,
      position: 'top',
    });
    await toast.present();
  }

  private clearOrderListener() {
    this.pusherListener?.unsubscribe();
    this.pusherListener = null;
    this.pusherService.disconnect();
  }

  private tearDownOrderListeners() {
    this.clearOrderListener();
    this.statusListener?.unsubscribe();
    this.statusListener = null;
    this.messageListener?.unsubscribe();
    this.messageListener = null;
    OpenOrderEffects.stopReloadOrders();
  }

  private listenToOrders(options: { location: LocationData; venue: Venue }) {
    this.pusherService.ensureConnected();
    if (!this.pusherListener) {
      this.handlePusher();
      this.pusherService.setupPusher(
        PusherChannel.ORDERS,
        options.venue.id,
        PusherEvent.ORDER_SUBMITTED
      );
      this.pusherService.setupPusher(
        PusherChannel.ORDERS,
        options.venue.id,
        PusherEvent.ORDER_STATUS_UPDATE
      );
      this.pusherService.setupPusher(
        PusherChannel.ORDERS,
        options.venue.id,
        PusherEvent.ORDER_UPDATED
      );
      this.pusherService.setupPusher(
        PusherChannel.ORDERS,
        options.venue.id,
        PusherEvent.ORDER_ITEM_UPDATE
      );
    }
  }

  private listenToStatusChanges() {
    if (!this.statusListener) {
      this.statusListener = this.ordersService.getStatusListener().subscribe(orderData => {
        this.store.dispatch(
          updateOrder({
            orderId: orderData._id,
            update: {
              status: orderData.status,
            },
          })
        );
      });
    }
  }

  private listenToMessageReadStatus() {
    if (!this.messageListener) {
      this.messageListener = this.supportService.getReadMessageListener().subscribe(orderId => {
        console.log('Messages read', orderId);
        // this.orders.forEach(order => {
        //   if (order.id === orderId && order.messages) {
        //     order.messages.unread = 0;
        //   }
        // });
      });
    }
  }

  private handlePusher() {
    this.pusherListener = this.pusherService
      .getPusherListener()
      .subscribe((observer: { event: string; orderId: string }) => {
        switch (observer.event) {
          case PusherEvent.ORDER_SUBMITTED:
            this.handleOrderSubmitted(observer.orderId);
            break;
          case PusherEvent.ORDER_STATUS_UPDATE:
          case PusherEvent.ORDER_UPDATED:
          case PusherEvent.ORDER_ITEM_UPDATE:
            this.handleOrderStatusUpdate(observer.orderId);
            break;
        }
      });
  }

  private async handleOrderSubmitted(orderId: string) {
    const order = await this.ordersService.findById(orderId).toPromise();
    if (!order) {
      return;
    }
    if (order.isConsumerOrder && order.restaurant === this.location?._id) {
      this.addToOpenOrders(order);
      if (order.status !== 'point-of-sale') {
        this.beepService.start();
      }
      return;
    }
    if (!order.isConsumerOrder) {
      this.addToOpenOrders(order);
    }
    if ([OrderStatus.DELIVERED, OrderStatus.CANCELLED].includes(order.status)) {
      this.removeFromOpenOrders(order);
    }
  }

  private async handleOrderStatusUpdate(orderId: string) {
    const order = await this.ordersService.findById(orderId).toPromise();
    if (!order) {
      return;
    }
    if ([OrderStatus.DELIVERED, OrderStatus.CANCELLED].includes(order.status)) {
      this.removeFromOpenOrders(order);
      return;
    }
    this.store.dispatch(updateOrder({ orderId, update: order }));
  }

  private handleNewMessage(message: { order: string }) {
    this.store.dispatch(newMessageForOrder({ orderId: message.order }));
    // this.showMessageToast(order);
  }

  private addToOpenOrders(orderData: OrderData) {
    this.store.dispatch(addOrder({ order: new Order(orderData) }));
  }

  private removeFromOpenOrders(order: OrderData) {
    this.store.dispatch(removeOrder({ orderId: order._id }));
  }
}
